import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useCookies } from "react-cookie"
import { useDispatch } from 'react-redux'
import { AppDispatch } from "@/redux"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { AcceptInviteForm } from "@/types/auth"
import { AuthMessage, AuthSchema } from "@/constants/auth/auth"
import { ResponseMessageBox } from "@/components/custom/ResponseMessageBox/ResponseMessageBox"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { timezones } from '../../constants/auth/timezone'
import { CopyRightText } from "@/components/custom/CopyRightText"
import { Button } from "@/components/ui/button"
import { UserAPI } from "@/API/User"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Auth } from "@/API/Auth"
import { userActions } from "@/redux/slice/userSlice"

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const AcceptInvite = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const query = useQuery()
  const inviteToken = query.get('token')
  const [preFormMessage, setPreFormMessage] = useState<string>('')
  const [postFormMessage, setPostFormMessage] = useState<string>('')
  const [, setCookie] = useCookies(['tcsRefreshToken', 'tcsAccessToken'])

  const form = useForm<AcceptInviteForm>({
    resolver: zodResolver(AuthSchema.acceptInviteForm),
    defaultValues: {
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      timezone: "",
      agreeTerms: false
    },
  })

  const onAcceptInviteSubmit = async (
    values: AcceptInviteForm
  ) => {
    if (values.password !== values.confirmPassword) {
      form.setError('password', { message: 'Passwords do not match.' })
      form.setError('confirmPassword', { message: 'Passwords do not match.' })
      return
    }
    if (inviteToken) {
      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        timezone: values.timezone,
        inviteToken: inviteToken
      }

      const response = await Auth.signup(data)

      if (!response.data && response.err) {
        setPostFormMessage(response.err)
        return
      }
      if (response.data) {
        // set token
        setCookie('tcsRefreshToken', response.data.refreshToken.token, {
          domain: document.location.hostname,
          path: '/',
          expires: new Date(response.data.refreshToken.expiresAt)
        })
        setCookie('tcsAccessToken', response.data.accessToken.token, {
          domain: document.location.hostname,
          path: '/',
          expires: new Date(response.data.accessToken.expiresAt)
        })
        dispatch(userActions.setUserToken(response.data.accessToken.token))
        setPostFormMessage('')
        form.reset({
          password: "",
          confirmPassword: "",
          firstName: "",
          lastName: "",
          timezone: ""
        })
        navigate('/router')
      }
    }
  }

  useEffect(() => {
    setPreFormMessage('')
    setPostFormMessage('')
    form.clearErrors()
  }, [form])

  useEffect(() => {
    if (inviteToken) {
      const validateInviteToken = async (inviteToken: string) => {
        const response = await UserAPI.validateInviteToken(inviteToken)
        if (response.data?.userId) {
          const acceptInviteResp = await UserAPI.acceptInvite({
            inviteToken,
            userId: response.data.userId
          })
          if (acceptInviteResp.data) {
            navigate('login?invite=success')
          } else {
            navigate('/login?invite=error')
          }
        } else if (response.err) {
          setPreFormMessage(AuthMessage.INVALID_TOKEN)
        }
      }

      validateInviteToken(inviteToken)
        .then(() => { console.log('checked invite token validity') })
        .catch(() => {
          console.log('error on checking invite token validity')
          navigate('/login')
        })
    } else {
      // no token means invalid token
      setPreFormMessage(AuthMessage.INVALID_TOKEN)
    }
  }, [inviteToken, navigate])

  return (
    <div className="authPage bg-opacity-50 py-20 flex flex-col justify-center items-center">
      <Card className="auth-card w-sm flex-column p-10">
        <CardHeader className="mb-12">
          <CardTitle>The Camp Stack</CardTitle>
        </CardHeader>

        {(postFormMessage || preFormMessage) && <ResponseMessageBox messageTitle={postFormMessage || preFormMessage} />}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onAcceptInviteSubmit)} className="space-y-8">
            <Label className="flex justify-start font-semibold text-secondary mb-10">
              SET UP YOUR ACCOUNT FIRST
            </Label>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex justify-start font-semibold text-primary"
                    isRequired
                  >
                    Password
                  </FormLabel>
                  <FormControl>
                    <Input
                      hasError={!!form.formState.errors.password}
                      type="password"
                      placeholder="********"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex justify-start font-semibold text-primary"
                    isRequired
                  >
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <Input
                      hasError={!!form.formState.errors.confirmPassword}
                      type="password"
                      placeholder="********"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex justify-start font-semibold text-primary"
                    isRequired
                  >
                    First Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      hasError={!!form.formState.errors.firstName}
                      type="text"
                      placeholder="John"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel
                    className="flex justify-start font-semibold text-primary"
                    isRequired
                  >
                    Last Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      hasError={!!form.formState.errors.lastName}
                      type="text"
                      placeholder="Doe"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex justify-start font-semibold text-primary">
                    Timezone
                  </FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <SelectTrigger aria-label="Select timezone">
                        <SelectValue placeholder="Select Timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.length && timezones.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agreeTerms"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center justify-start">
                    <FormControl>
                      <Checkbox
                        id='termsAndConditions'
                        aria-label="I agree to the Terms & Conditions"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <label htmlFor="termsAndConditions" className="ml-4 cursor-pointer text-sm">
                      I agree to the Terms & Conditions.
                    </label>
                  </div>
                  <FormMessage
                    showIcon={true}
                    className="flex justify-start"
                  />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full font-semibold">
              Sign Up
            </Button>
          </form>
        </Form>
      </Card>
      <CopyRightText />
    </div>
  )

}
